<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  experiences: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Teaching Experience'
  },
  description: {
    type: String,
    default: 'Select your teaching experience level'
  },
  showShadow: {
    type: Boolean,
    default: true
  },
  viewMode: {
    type: String,
    default: 'detailed', // 'detailed' or 'simple'
    validator: (value) => ['detailed', 'simple'].includes(value)
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const toggleExperience = (id) => {
  // Only allow selecting one experience
  const selected = props.modelValue.includes(id) ? [] : [id]

  emit('update:modelValue', selected)

  // Clear error when user makes a selection
  if (localErrors.value.selected_experiences) {
    delete localErrors.value.selected_experiences
  }
}

const isSelected = (id) => props.modelValue.includes(id)

const validate = () => {
  const errors = {}

  if (props.modelValue.length === 0) {
    errors.selected_experiences = 'Please select your teaching experience level'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div :class="['bg-white rounded-2xl p-10', showShadow ? 'shadow-lg' : '']">
    <div class="mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600">{{ description }}</p>

      <!-- Error Message -->
      <div v-if="displayErrors.selected_experiences" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.selected_experiences }}</p>
            <p class="text-red-700 text-sm mt-1">Choose your teaching experience level from the options below.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed View -->
    <div v-if="viewMode === 'detailed'" class="space-y-6">
      <div>
        <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
          <button
            v-for="experience in experiences"
            :key="experience.id"
            type="button"
            @click="toggleExperience(experience.id)"
            class="group flex flex-col items-center justify-between p-4 rounded-xl border-2 transition-all hover:shadow-md min-h-[120px]"
            :class="isSelected(experience.id) ? 'shadow-md' : 'border-gray-200 bg-white'"
            :style="{
              borderColor: isSelected(experience.id) ? experience.color : '',
              backgroundColor: isSelected(experience.id) ? `${experience.color}10` : ''
            }"
          >
            <div class="flex flex-col items-center flex-1 justify-center">
              <span class="text-md font-semibold text-gray-900 text-center">{{ experience.name }}</span>
              <span v-if="experience.age_range" class="text-xs text-gray-500 text-center mt-0.5">{{ experience.age_range }}</span>
              <span class="text-sm text-gray-600 text-center mt-1">{{ experience.description }}</span>
            </div>
            <div
              v-if="isSelected(experience.id)"
              class="mt-2 text-brand"
            >
              <span class="material-symbols-outlined text-xl">check_circle</span>
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- Simple View -->
    <div v-else class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <button
        v-for="experience in experiences"
        :key="experience.id"
        type="button"
        @click="toggleExperience(experience.id)"
        class="relative group px-4 py-3 rounded-lg border-2 transition-all hover:shadow-sm min-h-[72px] flex items-center justify-center"
        :class="isSelected(experience.id) ? '' : 'border-gray-200 bg-white hover:border-gray-300'"
        :style="{
          borderColor: isSelected(experience.id) ? experience.color : '',
          backgroundColor: isSelected(experience.id) ? `${experience.color}08` : ''
        }"
      >
        <div class="flex flex-col items-center gap-1">
          <span class="text-sm font-semibold text-gray-900 text-center block leading-tight">{{ experience.name }}</span>
          <span v-if="experience.subheading" class="text-xs text-gray-500 text-center block leading-tight">{{ experience.subheading }}</span>
        </div>

        <!-- Triangle indicator for selected experiences -->
        <div
          v-if="isSelected(experience.id)"
          class="absolute top-0 right-0 w-0 h-0"
          :style="{
            borderTop: `20px solid ${experience.color}`,
            borderLeft: '20px solid transparent'
          }"
        ></div>
      </button>
    </div>
  </div>
</template>
